import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { Sparkles, RefreshCw, ChevronRight, BarChart3 } from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, LineChart, Line,
} from 'recharts';
import PageHeader from '../components/page/PageHeader';
import KpiCard from '../components/dashboard/KpiCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Alpha ramp for a single-hue sequential palette on the workspace hue.
const STAGE_ALPHAS = [0.28, 0.44, 0.60, 0.78, 0.95];

// Placeholder sparkline samples until reports back a real trend series.
const SPARK_STUB = [4, 6, 5, 7, 8, 7, 9, 10, 12];

export default function Dashboard() {
    const { user, getAuthHeader } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const { contactScope } = usePermissions();
    const assignedOnly = contactScope === 'assigned';
    const [kpis, setKpis] = useState(null);
    const [pipeline, setPipeline] = useState([]);
    const [forecast, setForecast] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [briefing, setBriefing] = useState('');
    const [loadingBriefing, setLoadingBriefing] = useState(false);
    const [formAnalytics, setFormAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
    const [permissionNotice, setPermissionNotice] = useState('');

    useEffect(() => {
        loadDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, assignedOnly]);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const ownerParam = (assignedOnly || viewMode === 'my') && user?.user_id ? `owner_id=${user.user_id}` : '';
            setPermissionNotice('');
            const [kpiRes, pipelineRes, forecastRes, activitiesRes] = await Promise.all([
                axios.get(`${API}/reports/dashboard-kpis${ownerParam ? '?' + ownerParam : ''}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/pipeline-summary${ownerParam ? '?' + ownerParam : ''}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/revenue-forecast${ownerParam ? '?' + ownerParam : ''}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/recent-activities?limit=8${ownerParam ? '&' + ownerParam : ''}`, { headers: getAuthHeader() }),
            ]);
            setKpis(kpiRes.data);
            setPipeline(pipelineRes.data);
            setForecast(forecastRes.data);
            setRecentActivities(activitiesRes.data);

            // Form analytics — best-effort, must not break the dashboard
            try {
                const formAnalyticsRes = await axios.get(`${API}/forms/analytics/overview`, { headers: getAuthHeader() });
                setFormAnalytics(formAnalyticsRes.data);
            } catch {
                setFormAnalytics(null);
            }
        } catch (e) {
            console.error(e);
            if (e?.response?.status === 403) setPermissionNotice('Some dashboard data is restricted to contacts and deals you can access.');
        } finally {
            setLoading(false);
        }
    };

    const generateBriefing = async () => {
        setLoadingBriefing(true);
        try {
            const res = await axios.post(`${API}/ai/generate-briefing`, {}, { headers: getAuthHeader() });
            setBriefing(res.data.briefing);
        } catch {
            setBriefing('• Review high-risk deals requiring attention today\n• Follow up on proposals sent this week\n• Schedule executive briefings for key enterprise accounts\n• Check pipeline velocity for quarterly targets');
        } finally {
            setLoadingBriefing(false);
        }
    };

    const formatCurrency = (v) => {
        if (!v) return '$0';
        if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
        if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
        return `$${v}`;
    };

    const hue = currentWorkspace?.hue || '#3E63DD';

    const pipelineWithColor = useMemo(() => {
        return (pipeline || []).map((entry, i) => ({
            ...entry,
            _fill: `color-mix(in srgb, ${hue} ${STAGE_ALPHAS[i % STAGE_ALPHAS.length] * 100}%, transparent)`,
        }));
    }, [pipeline, hue]);

    const ActivityIcon = ({ type }) => {
        const icons = { email: '📧', call: '📞', meeting: '📅', note: '📝', task: '✅' };
        return <span className="text-base">{icons[type] || '📌'}</span>;
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div
                            key={i}
                            className="h-32 rounded-[14px] animate-pulse"
                            style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-[1600px]">
            <PageHeader
                title={viewMode === 'my' ? 'My Dashboard' : 'Team Dashboard'}
                subtitle={`${new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} · Signals across your pipeline`}
                right={(
                    <div
                        className="flex items-center gap-1 rounded-lg p-0.5"
                        style={{ background: 'color-mix(in srgb, var(--ink-1) 5%, transparent)' }}
                    >
                        <button
                            onClick={() => setViewMode('my')}
                            className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all"
                            style={viewMode === 'my'
                                ? { background: 'var(--surface)', color: 'var(--ink-1)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }
                                : { color: 'var(--ink-2)' }}
                        >
                            My Pipeline
                        </button>
                        {!assignedOnly && <button
                            onClick={() => setViewMode('all')}
                            className="px-3 py-1.5 text-xs font-semibold rounded-md transition-all"
                            style={viewMode === 'all'
                                ? { background: 'var(--surface)', color: 'var(--ink-1)', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }
                                : { color: 'var(--ink-2)' }}
                        >
                            All
                        </button>}
                    </div>
                )}
            />
            {permissionNotice && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">{permissionNotice}</div>}

            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard testId="kpi-pipeline-value" label="Pipeline value" value={formatCurrency(kpis?.pipeline_value)} delta="+12%" trend="up" spark={SPARK_STUB} />
                <KpiCard testId="kpi-total-accounts" label="Companies" value={kpis?.total_accounts || 0} spark={SPARK_STUB} />
                <KpiCard testId="kpi-total-contacts" label="Contacts" value={kpis?.total_contacts || 0} spark={SPARK_STUB} />
                <KpiCard testId="kpi-win-rate" label="Win rate" value={`${kpis?.win_rate || 0}%`} delta="+3%" trend="up" spark={SPARK_STUB} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard testId="kpi-won-value" label="Closed won" value={formatCurrency(kpis?.won_value)} spark={SPARK_STUB} />
                <KpiCard testId="kpi-open-deals" label="Open deals" value={kpis?.total_deals || 0} spark={SPARK_STUB} />
                <KpiCard testId="kpi-high-risk" label="High-risk deals" value={kpis?.high_risk_deals || 0} subtitle="Needs attention" />
                <KpiCard testId="kpi-overdue-tasks" label="Overdue tasks" value={kpis?.overdue_tasks || 0} />
            </div>

            {/* Charts + AI Briefing */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Pipeline by Stage — single-hue alpha ramp */}
                <div
                    className="lg:col-span-2 rounded-[14px] p-4 sm:p-6"
                    style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
                    data-testid="pipeline-chart"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3
                            className="font-semibold"
                            style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}
                        >
                            Pipeline by Stage
                        </h3>
                        <button
                            onClick={() => navigate('/pipeline')}
                            className="text-sm flex items-center gap-1"
                            style={{ color: 'var(--ink-2)' }}
                        >
                            View all <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={pipelineWithColor} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--ink-label)' }} axisLine={{ stroke: 'var(--line)' }} />
                            <YAxis
                                tick={{ fontSize: 11, fill: 'var(--ink-label)' }}
                                axisLine={{ stroke: 'var(--line)' }}
                                tickFormatter={v => v >= 1000 ? `$${v / 1000}K` : `$${v}`}
                            />
                            <Tooltip
                                formatter={(val) => [formatCurrency(val), 'Value']}
                                contentStyle={{
                                    fontSize: 12,
                                    borderRadius: 8,
                                    border: '1px solid var(--line)',
                                    background: 'var(--surface)',
                                    color: 'var(--ink-1)',
                                }}
                            />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                {pipelineWithColor.map((entry, i) => (
                                    <Cell key={i} fill={entry._fill} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* AI Briefing — iridescent hairline border via padding-box/border-box */}
                <div
                    className="rounded-[14px] flex flex-col"
                    style={{
                        padding: 1,
                        background: 'var(--ai-conic)',
                    }}
                    data-testid="ai-briefing-card"
                >
                    <div
                        className="rounded-[13px] p-4 sm:p-6 flex-1 flex flex-col"
                        style={{ background: 'var(--surface)' }}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center"
                                    style={{ background: 'var(--ai-conic)' }}
                                    aria-hidden="true"
                                >
                                    <Sparkles className="w-3 h-3 text-white" />
                                </div>
                                <h3
                                    className="font-semibold text-sm"
                                    style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}
                                >
                                    AI Daily Briefing
                                </h3>
                            </div>
                            <button
                                onClick={generateBriefing}
                                disabled={loadingBriefing}
                                data-testid="generate-briefing-btn"
                                className="p-1.5 rounded-lg transition-colors disabled:opacity-50"
                                style={{ color: 'var(--ink-2)' }}
                            >
                                <RefreshCw className={`w-4 h-4 ${loadingBriefing ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                        {briefing ? (
                            <div className="flex-1 text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--ink-1)' }}>
                                {briefing}
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-center">
                                <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                                    style={{ background: 'color-mix(in srgb, var(--ink-1) 6%, transparent)' }}
                                >
                                    <Sparkles className="w-6 h-6" style={{ color: 'var(--ink-2)' }} />
                                </div>
                                <p className="text-sm mb-3" style={{ color: 'var(--ink-2)' }}>
                                    Get your personalized sales briefing powered by AI
                                </p>
                                <button
                                    onClick={generateBriefing}
                                    disabled={loadingBriefing}
                                    data-testid="generate-briefing-cta-btn"
                                    className="px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
                                    style={{ background: 'var(--ink-1)', color: 'var(--canvas)' }}
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {loadingBriefing ? 'Generating...' : 'Generate Briefing'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Form Performance */}
            <div
                className="rounded-[14px] p-4 sm:p-6"
                style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
                data-testid="form-performance"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
                        <h3
                            className="font-semibold"
                            style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}
                        >
                            Form Performance
                        </h3>
                    </div>
                    <button
                        onClick={() => navigate('/forms')}
                        className="text-sm flex items-center gap-1"
                        style={{ color: 'var(--ink-2)' }}
                    >
                        View all <ChevronRight className="w-4 h-4" />
                    </button>
                </div>

                {formAnalytics && formAnalytics.trends && formAnalytics.trends.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                        {/* Submission Trends Chart */}
                        <div className="lg:col-span-2">
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={formAnalytics.trends} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="formTrendsGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={hue} stopOpacity={0.16} />
                                            <stop offset="95%" stopColor={hue} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--ink-label)' }} axisLine={{ stroke: 'var(--line)' }} />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: 'var(--ink-label)' }}
                                        axisLine={{ stroke: 'var(--line)' }}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        formatter={(val) => [val, 'Submissions']}
                                        contentStyle={{
                                            fontSize: 12,
                                            borderRadius: 8,
                                            border: '1px solid var(--line)',
                                            background: 'var(--surface)',
                                            color: 'var(--ink-1)',
                                        }}
                                    />
                                    <Area type="monotone" dataKey="submissions" stroke={hue} strokeWidth={2} fill="url(#formTrendsGrad)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Top Forms by Submissions */}
                        <div>
                            <h4
                                className="text-xs font-semibold uppercase tracking-wider mb-3"
                                style={{ color: 'var(--ink-label)' }}
                            >
                                Top Forms
                            </h4>
                            <div className="space-y-2">
                                {(formAnalytics.conversion || [])
                                    .slice()
                                    .sort((a, b) => (b.submissions || 0) - (a.submissions || 0))
                                    .slice(0, 5)
                                    .map((form, i) => (
                                        <div key={i} className="flex items-center justify-between gap-2">
                                            <span
                                                className="text-sm truncate flex-1"
                                                style={{ color: 'var(--ink-1)' }}
                                            >
                                                {form.name}
                                            </span>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <span
                                                    className="text-sm font-medium"
                                                    style={{ color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}
                                                >
                                                    {form.submissions || 0}
                                                </span>
                                                <span
                                                    className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                                                    style={form.is_active
                                                        ? { background: 'var(--good-bg)', color: 'var(--good)' }
                                                        : { background: 'color-mix(in srgb, var(--ink-1) 5%, transparent)', color: 'var(--ink-label)' }}
                                                >
                                                    {form.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <BarChart3 className="w-8 h-8 mb-2" style={{ color: 'var(--ink-label)' }} />
                        <p className="text-sm" style={{ color: 'var(--ink-label)' }}>
                            No form data yet
                        </p>
                    </div>
                )}
            </div>

            {/* Forecast + Recent Activities */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Revenue Forecast — single-hue area on workspace hue */}
                <div
                    className="lg:col-span-2 rounded-[14px] p-4 sm:p-6"
                    style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
                    data-testid="forecast-chart"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3
                            className="font-semibold"
                            style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}
                        >
                            Revenue Forecast
                        </h3>
                        <span
                            className="text-[11px] px-2 py-1 rounded-full"
                            style={{
                                background: 'color-mix(in srgb, var(--ink-1) 5%, transparent)',
                                color: 'var(--ink-label)',
                            }}
                        >
                            Weighted by probability
                        </span>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={forecast} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={hue} stopOpacity={0.16} />
                                    <stop offset="95%" stopColor={hue} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--ink-label)' }} axisLine={{ stroke: 'var(--line)' }} />
                            <YAxis
                                tick={{ fontSize: 11, fill: 'var(--ink-label)' }}
                                axisLine={{ stroke: 'var(--line)' }}
                                tickFormatter={v => formatCurrency(v)}
                            />
                            <Tooltip
                                formatter={(val) => [formatCurrency(val), 'Forecast']}
                                contentStyle={{
                                    fontSize: 12,
                                    borderRadius: 8,
                                    border: '1px solid var(--line)',
                                    background: 'var(--surface)',
                                    color: 'var(--ink-1)',
                                }}
                            />
                            <Area type="monotone" dataKey="forecast" stroke={hue} strokeWidth={2} fill="url(#forecastGrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* Recent Activities */}
                <div
                    className="rounded-[14px] p-4 sm:p-6"
                    style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
                    data-testid="recent-activities"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3
                            className="font-semibold text-sm"
                            style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}
                        >
                            Recent Activities
                        </h3>
                        <button
                            onClick={() => navigate('/activities')}
                            className="text-xs"
                            style={{ color: 'var(--ink-2)' }}
                        >
                            View all
                        </button>
                    </div>
                    <div className="space-y-3">
                        {recentActivities.slice(0, 6).map((a, i) => (
                            <div key={a.activity_id || i} className="flex items-start gap-3">
                                <div
                                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm"
                                    style={{ background: 'color-mix(in srgb, var(--ink-1) 5%, transparent)' }}
                                >
                                    <ActivityIcon type={a.type} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate" style={{ color: 'var(--ink-1)' }}>{a.title}</p>
                                    <p className="text-xs" style={{ color: 'var(--ink-label)' }}>{a.account_name || a.type}</p>
                                </div>
                                <span
                                    className="text-[11px] px-1.5 py-0.5 rounded-full"
                                    style={a.status === 'completed'
                                        ? { background: 'var(--good-bg)', color: 'var(--good)' }
                                        : { background: 'var(--warn-bg)', color: 'var(--warn)' }}
                                >
                                    {a.status}
                                </span>
                            </div>
                        ))}
                        {recentActivities.length === 0 && (
                            <p className="text-sm text-center py-4" style={{ color: 'var(--ink-label)' }}>
                                No recent activities
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Pipeline Stage counts — with direct labels */}
            <div
                className="rounded-[14px] p-6"
                style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}
                data-testid="pipeline-stages"
            >
                <div className="flex items-center justify-between mb-4">
                    <h3
                        className="font-semibold"
                        style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--ink-1)' }}
                    >
                        Pipeline Stages
                    </h3>
                    <button
                        onClick={() => navigate('/pipeline')}
                        className="text-sm flex items-center gap-1"
                        style={{ color: 'var(--ink-2)' }}
                    >
                        Open Pipeline <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {pipelineWithColor.map((stage) => (
                        <div
                            key={stage.stage ?? 'unmapped'}
                            className="flex-1 min-w-24 p-3 rounded-lg cursor-pointer transition-colors text-center"
                            style={{ background: 'color-mix(in srgb, var(--ink-1) 3%, transparent)' }}
                            onClick={() => navigate('/pipeline')}
                        >
                            <div
                                className="text-lg font-bold"
                                style={{
                                    fontFamily: 'Manrope, sans-serif',
                                    color: 'var(--ink-1)',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {stage.count}
                            </div>
                            <div className="text-xs mt-0.5" style={{ color: 'var(--ink-label)' }}>
                                {stage.label}
                            </div>
                            <div className="text-xs font-medium" style={{ color: 'var(--ink-2)' }}>
                                {formatCurrency(stage.value)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
